/**
 * Public landing for ad traffic (editorial pass, ADR-0029). A light paper hero with a wide display title and one
 * editorial photograph, a black manifesto whose words light up as it scrolls, then the proof in public: every figure on
 * the page comes from the platform itself (the live execution-quality aggregate, the public ticker, the published
 * program versions). No invented result, review, partner or figure anywhere.
 */
import { type Lang, localeTagFor } from "@prop/i18n";
import { type CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "wouter";
import { useLandingT, useLang } from "../../i18n";
import { art, artBase, HERO_SIZES, MARKETS, num, useBtc, useNow, useQuality } from "./landing-data";
import { Pic } from "./Pic";
import { Docs, Faq, Final, Footer, How, Platform, Programs, Quote, Slogans, Trust } from "./LandingSections";
import { CountUp } from "./LiveBits";
import { LangMenu } from "../../app/LangMenu";
import { Buy } from "./Buy";
import { Logo } from "../../ui/Logo";
import { Mark } from "./Mark";
import "./landing.css";

// The motion layer (GSAP, ScrollTrigger, Lenis) is its own chunk, requested as soon as this module runs, in parallel
// with the fonts it waits for anyway — so it is not in the first bundle (ADR-0048).
let motion: Promise<typeof import("./landing-motion")> | null = null;
const loadMotion = () => (motion ??= import("./landing-motion"));

export function LandingPage({ signedIn }: { signedIn: boolean }) {
  const root = useRef<HTMLDivElement>(null);
  const start = signedIn ? "/accounts" : "/register";

  // The motion layer starts once the fonts are in (its reveals measure lines), and is torn down on leave.
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    let stop: (() => void) | null = null;
    let gone = false;
    void Promise.all([loadMotion(), document.fonts.ready]).then(([m]) => {
      if (!gone) stop = m.startLandingMotion(el);
    });
    return () => {
      gone = true;
      stop?.();
    };
  }, []);

  return (
    <div className="lp" ref={root} id="top">
      <main>
        <Hero signedIn={signedIn} start={start} />
        <Bar />
        <Statement />
        <Slogans />
        <Trust />
        <Quote k="money" tone="cobalt" />
        <Programs />
        <Platform />
        <How />
        <Quote k="start" tone="lime" />
        <Docs />
        <Faq />
        <Final start={start} />
      </main>
      <Footer />
      <ToTop />
    </div>
  );
}

const arrow = "↗";

function Nav({ signedIn }: { signedIn: boolean }) {
  const t = useLandingT();
  const { lang } = useLang();
  const now = useNow();
  const fmt = useMemo(() => {
    const tag = localeTagFor(lang as Lang);
    return {
      day: new Intl.DateTimeFormat(tag, { weekday: "long" }),
      date: new Intl.DateTimeFormat(tag, { day: "2-digit", month: "2-digit" }),
      time: new Intl.DateTimeFormat(tag, { hour: "2-digit", minute: "2-digit", timeZone: "UTC", hourCycle: "h23" }),
    };
  }, [lang]);
  // One header row: the nav pill, the date (only while it fits — it wraps out of its one-line room otherwise) and the
  // language, sign-in and buy. On a phone the row stacks; the date and the docs anchor stay out (UX law 6).
  return (
    <div className="hx-head">
      <nav className="hx-nav" aria-label={t("nav.menu")} data-in="nav">
        <span className="hx-pill" aria-hidden="true" />
        <a href="#top" className="hx-logo" aria-label={t("brand")}>
          <Logo size={28} tone="light" compact />
        </a>
        <a href="#programs">{t("nav.programs")}</a>
        <a href="#execution">{t("nav.execution")}</a>
        <a href="#docs" className="hx-opt">
          {t("nav.docs")}
        </a>
        <a href="#faq">{t("nav.faq")}</a>
      </nav>
      <div className="hx-room" data-in>
        <i aria-hidden="true" />
        <p className="hx-date">
          {now ? (
            <>
              <span className="cap">{fmt.day.format(now)}</span> / {fmt.date.format(now)} · {fmt.time.format(now)} UTC
            </>
          ) : (
            " "
          )}
        </p>
      </div>
      <div className="hx-top" data-in>
        <LangMenu paper label={t("nav.language")} />
        <Link href={signedIn ? "/accounts" : "/login"} className="hx-signin">
          {signedIn ? t("nav.cabinet") : t("nav.signIn")}
        </Link>
        <Buy size="sm" />
      </div>
    </div>
  );
}

function Hero({ signedIn, start }: { signedIn: boolean; start: string }) {
  const t = useLandingT();
  const { lang } = useLang();
  const l = lang as Lang;
  const q = useQuality().data;
  const btc = useBtc();
  return (
    <section className="hx" aria-labelledby="hx-title">
      <div className="hx-board">
        <Nav signedIn={signedIn} />
        <h1 id="hx-title" className="hx-title">
          <span className="line">
            <span>{t("hero.titleA")}</span>
            <span className="hx-year" aria-hidden="true">
              {t("hero.year")}
            </span>
          </span>
          <span className="line">
            <span>{t("hero.titleB")}</span>
          </span>
        </h1>

        <div className="hx-about">
          <p className="hx-tag" data-in>
            <span className="dot" aria-hidden="true" /> {t("hero.about")}
          </p>
          <p className="hx-lead" data-in>
            {t("hero.aboutText")}
          </p>
          <div className="hx-cta" data-in>
            <Buy />
            <Link href={start} className="hx-link">
              {t("hero.secondary")}
            </Link>
          </div>
        </div>

        <figure className="hx-photo">
          <Pic base={artBase("hero")} widths={[480, 700]} sizes={HERO_SIZES} src={art("hero")} width={700} height={720} priority />
        </figure>
        <div className="hx-plate" data-in>
          <Mark size={30} />
          <span className="hx-plate-name">{t("brand")}</span>
          <span className={`hx-plate-px ${btc.dir}`} aria-live="polite">
            <small>BTC</small> {btc.price ? num(Math.round(Number(btc.price)), l) : "—"}
          </span>
        </div>
        <p className="hx-sources" data-in>
          {t("hero.sources")}
        </p>

        <div className="hx-frame" data-in>
          <div className="hx-card dark">
            <p className="hx-big">
              <CountUp value={q?.fills} format={(v) => num(v, l)} />
            </p>
            <p className="hx-small">{t("board.fills")}</p>
            <ul className="hx-markets" aria-label={t("hero.markets")}>
              {MARKETS.map((m) => (
                <li key={m}>
                  <MarketGlyph market={m} />
                  {t(`spec.markets.${m}`)}
                </li>
              ))}
            </ul>
            <a href="#programs" className="hx-bar-cta">
              {t("hero.primary")} <span aria-hidden="true">{arrow}</span>
            </a>
          </div>
          <div className="hx-card photo">
            <Pic base={artBase("hands")} widths={[480, 960]} sizes="(max-width: 760px) 45vw, 22vw" src={art("hands")} width={1280} height={720} />
            <p className="hx-feed">
              <span>{t("hero.feed")}</span>
              <span>{t("board.live")}</span>
            </p>
            <p className="hx-lat">
              <strong>{q?.latencyMs ? t("hero.ms", { v: num(q.latencyMs.p50, l) }) : "—"}</strong>
              <span>{t("hero.latency")}</span>
            </p>
          </div>
        </div>

        <p className="hx-badge" data-in>
          <span className="check" aria-hidden="true">
            ✓
          </span>
          {t("hero.badge", { v: q?.policyVersion ?? "1.1" })}
        </p>
        <p className="hx-avail" data-in>
          {t("hero.avail1")}
          <br />
          <span className="dot" aria-hidden="true" /> {t("hero.avail2")}
        </p>
        <a href="#programs" className="hx-out" data-in>
          <span className="hx-out-head">
            <span className="soft">{t("hero.outNow")}</span>
            <strong>{t("hero.outTitle")}</strong>
            <span className="soft">{t("hero.outTag")}</span>
          </span>
          <span className="hx-out-pic">
            <Pic base={artBase("streak")} widths={[640, 1280]} sizes="(max-width: 760px) 100vw, 40vw" src={art("streak")} width={1280} height={720} />
            <span className="hx-out-arrow" aria-hidden="true">
              {arrow}
            </span>
            <span className="hx-out-line" aria-hidden="true">
              <i />
            </span>
          </span>
        </a>
      </div>
    </section>
  );
}

function MarketGlyph({ market }: { market: (typeof MARKETS)[number] }) {
  if (market === "crypto") return <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M6 1 11 6 6 11 1 6Z" fill="currentColor" /></svg>;
  if (market === "fx") return <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><circle cx="4.5" cy="6" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.6" /><circle cx="7.5" cy="6" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.6" /></svg>;
  return <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M1 11h3V7h3V4h4V1" fill="none" stroke="currentColor" strokeWidth="1.6" /></svg>;
}

function Bar() {
  const t = useLandingT();
  return (
    <div className="bar" aria-hidden="true">
      <span>
        {t("bar.since")} <span className="grey">{t("bar.year")}</span>
      </span>
      <a href="#manifest" className="bar-scroll" tabIndex={-1}>
        {t("bar.scroll")} <span className="bar-dot">↓</span>
      </a>
      <span className="grey">{t("bar.live")}</span>
    </div>
  );
}

/** The manifesto: every word its own span, lit from grey to white in reading order as the section scrolls. */
function Statement() {
  const t = useLandingT();
  const words = t("statement.text").split(" ");
  return (
    <section className="statement" id="manifest" aria-label={t("statement.sub")}>
      <p className="statement-text">
        {words.map((w, i) => (
          <span key={i} className="w">
            {w}{" "}
          </span>
        ))}
      </p>
      <p className="statement-sub">
        {t("statement.sub")} <span className="grey">{t("statement.subMuted")}</span>
      </p>
    </section>
  );
}

/**
 * Back to the top: a round dark-glass button with the lime arrow, bottom right, shown once the hero is scrolled past.
 * It stacks above whatever is pinned to the bottom edge at the moment (the cookie banner, the configurator's price bar:
 * anything marked `data-bottom-bar`) and keeps clear of the home indicator. An anchor, so the smooth scroll layer
 * (Lenis) glides it and reduced motion jumps.
 */
function ToTop() {
  const t = useLandingT();
  const [shown, setShown] = useState(false);
  const [lift, setLift] = useState(0);
  useEffect(() => {
    let raf = 0;
    const measure = () => {
      raf = 0;
      setShown(window.scrollY > window.innerHeight * 0.9);
      let up = 0;
      for (const el of document.querySelectorAll<HTMLElement>("[data-bottom-bar]")) {
        const r = el.getBoundingClientRect();
        if (r.height && r.top < window.innerHeight && r.bottom > window.innerHeight - 48) up = Math.max(up, window.innerHeight - r.top);
      }
      setLift(Math.round(up));
    };
    const on = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("scroll", on, { passive: true });
    window.addEventListener("resize", on);
    // The cookie banner comes and goes without a scroll.
    const tick = window.setInterval(on, 700);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", on);
      window.removeEventListener("resize", on);
      window.clearInterval(tick);
    };
  }, []);
  return (
    <a href="#top" className={`to-top${shown ? " on" : ""}`} inert={!shown} style={{ "--lift": `${lift}px` } as CSSProperties}>
      <span className="sr-only">{t("footer.top")}</span>
      <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
        <path d="M9 15V3M3.5 8.5 9 3l5.5 5.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </a>
  );
}
